<template>
  <system-frame title="Süsteemi seaded" hint="Moodulid, uued vaated, kliendiportaal, intra sisselogimine, e-post ja SMS. Muudatus kehtib kohe kõigile.">
    <loading-content :loading="loading">
      <v-row>
        <v-col cols="12" md="8">
          <!-- Like Hooldusaktid → Seadistused: a page of settings touched once
               in a while, so every section starts closed and says only its
               name; `multiple` because comparing two beats choosing. Nothing
               is remembered between visits. A section with something wrong
               inside says so on its closed title, so a warning is never
               hidden behind a click. -->
          <v-expansion-panels multiple accordion v-model="open">
            <v-expansion-panel v-for="group in GROUPS" :key="group.title">
              <v-expansion-panel-title class="text-subtitle-1">
                {{ group.title }}
                <v-icon
                  v-if="hasWarning(group)"
                  color="orange-darken-2"
                  size="small"
                  class="ms-2"
                >mdi-alert</v-icon>
              </v-expansion-panel-title>
              <v-expansion-panel-text v-if="group.switches">
                <p class="text-body-2 text-grey-darken-1">{{ group.intro }}</p>

                <!-- A switch that only means something while another is on sits
                     indented under it, and is greyed out while that one is off. -->
                <div
                  v-for="(item, index) in group.switches"
                  :key="item.key"
                  :class="{ 'ms-8': item.parent }"
                >
                  <v-switch
                    v-model="settings[item.key]"
                    :loading="saving === item.key"
                    :disabled="saving !== null || (item.parent && !settings[item.parent])"
                    inset
                    :label="item.label"
                    persistent-hint
                    :hint="item.hint"
                    :class="{ 'mt-6': index > 0 }"
                    @update:model-value="save(item.key, $event)"
                  />
                  <v-alert
                    v-if="item.warn && item.warn(settings, available)"
                    :type="item.warn(settings, available).type"
                    class="mt-4 mb-0"
                    variant="tonal"
                    density="compact"
                  >
                    {{ item.warn(settings, available).text }}
                  </v-alert>
                </div>
              </v-expansion-panel-text>
              <!-- Ladu: a signed act sells its parts from the technician's van
                   in Ladu. The switch, where Ladu is, the key it gave intra,
                   and which van is whose. -->
              <v-expansion-panel-text v-else-if="group.ladu">
                <p class="text-body-2 text-grey-darken-1">
                  Akti allkirjastamisel võetakse akti varuosad Laos maha tehniku autost. Akt allkirjastatakse
                  alati — kui Ladu ei vasta või osa on puudu, saab kontor teate lehel Aktid → Ladu.
                </p>
                <v-switch
                  v-model="settings.ladu"
                  :loading="saving === 'ladu'"
                  :disabled="saving !== null"
                  inset
                  label="Ladu"
                  persistent-hint
                  hint="Sees: allkirjastatud akti varuosad lähevad Laos tehniku autost maha, akti vorm näitab, mitu tükki autos on, ja kontoril on leht Aktid → Ladu. Väljas: intra ei pöördu Lao poole. Enne sisselülitamist allkirjastatud akte Lattu ei saadeta."
                  @update:model-value="save('ladu', $event)"
                />
                <v-alert
                  v-if="settings.ladu && laduLoaded && (!ladu.baseUrl || !ladu.apiKeySet)"
                  type="warning"
                  class="mt-4 mb-0"
                  variant="tonal"
                  density="compact"
                >
                  Lao aadress või API võti on puudu — aktid jäävad saatmata, kuni need on sisestatud.
                </v-alert>

                <v-text-field
                  v-model="laduUrl"
                  :loading="saving === 'laduUrl'"
                  :disabled="saving !== null"
                  label="Lao aadress"
                  placeholder="https://ladu.gaspre.ee"
                  persistent-hint
                  hint="Aadress, millelt Ladu avaneb. Kui Ladu kolib, muuda ainult seda välja."
                  class="mt-6"
                  @blur="saveLaduUrl"
                  @keyup.enter="$event.target.blur()"
                />
                <div class="d-flex align-start ga-2 mt-6">
                  <v-text-field
                    v-model="laduKey"
                    :disabled="saving !== null"
                    type="password"
                    autocomplete="new-password"
                    label="API võti"
                    :placeholder="ladu.apiKeySet ? 'Võti on salvestatud — uue võtme jaoks kirjuta see siia' : ''"
                    persistent-placeholder
                    persistent-hint
                    hint="Lao lehelt Seadistused → API võtmed → Uus võti. Võtit näidatakse seal ainult üks kord; siin seda enam näha ei saa."
                    @keyup.enter="saveLaduKey"
                  />
                  <v-btn
                    color="primary"
                    variant="tonal"
                    class="mt-2"
                    :loading="saving === 'laduKey'"
                    :disabled="saving !== null || !laduKey.trim()"
                    @click="saveLaduKey"
                  >Salvesta võti</v-btn>
                </div>

                <div class="d-flex align-center flex-wrap ga-3 mt-6">
                  <v-btn
                    variant="outlined"
                    :loading="saving === 'laduTest'"
                    :disabled="saving !== null || !ladu.baseUrl || !ladu.apiKeySet"
                    @click="testLadu"
                  >Testi ühendust</v-btn>
                  <span v-if="laduTest" :class="laduTest.ok ? 'text-green-darken-2' : 'text-red-darken-2'" class="text-body-2" data-ladu-test>
                    {{ laduTest.message }}
                  </span>
                </div>

                <!-- Whose van is whose. Asked from Ladu when the section is
                     opened, so opening Seaded for something else costs Ladu
                     nothing. -->
                <h3 class="text-subtitle-2 mt-8 mb-1">Tehnikute autod</h3>
                <p class="text-body-2 text-grey-darken-1 mb-2">
                  Vali igale tehnikule tema ladu (auto) Laos. Tehniku akti osad võetakse maha sellest laost.
                  Tehnik, kellel autot ei ole, saab hoiatuse ainult siis, kui tema aktil on Lao varuosi.
                </p>
                <v-btn
                  v-if="!vans.loaded"
                  variant="text"
                  color="primary"
                  :loading="vans.loading"
                  :disabled="!ladu.baseUrl || !ladu.apiKeySet"
                  @click="loadVans"
                >Näita tehnikuid</v-btn>
                <v-alert v-if="vans.note" type="warning" variant="tonal" density="compact" class="mb-2">
                  Lao ladude nimekirja ei saanud: {{ vans.note }}
                </v-alert>
                <v-table v-if="vans.loaded" density="compact" class="ladu-vans">
                  <thead>
                    <tr><th>Tehnik</th><th>Ladu (auto)</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="technic in vans.technics" :key="technic.id">
                      <td>{{ technic.fullName }}</td>
                      <td>
                        <v-select
                          :model-value="technic.storeId"
                          :items="storeChoices(technic)"
                          item-title="title"
                          item-value="value"
                          density="compact"
                          hide-details
                          variant="outlined"
                          :loading="saving === 'van' + technic.id"
                          :disabled="saving !== null || !vans.stores.length"
                          @update:model-value="saveVan(technic, $event)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-expansion-panel-text>
              <!-- Intra sisselogimine: intra's own login page in the hooldusplaat
                   look, the same design as the Kliendiportaal login. -->
              <v-expansion-panel-text v-else-if="group.intraLogin">
                <p class="text-body-2 text-grey-darken-1">
                  Kuidas näeb välja intra sisselogimise leht. Muudatus jõustub lehe
                  uuesti avamisel.
                </p>
                <v-switch
                  v-model="intraLook.plate"
                  :loading="saving === 'intraPlate'"
                  :disabled="saving !== null"
                  inset
                  label="Uus kujundus"
                  persistent-hint
                  hint="Sama kujundus nagu kliendiportaali sisselogimisel: logo, suured väljad, roheline nupp. Kehtib intra uues vaates."
                  @update:model-value="saveIntraLook('plate', $event)"
                />
                <v-alert
                  v-if="intraLook.plate && !settings.clientNext"
                  type="info"
                  class="mt-4 mb-0"
                  variant="tonal"
                  density="compact"
                >
                  Uus kujundus on ainult intra uues vaates — see tuleb nähtavale, kui
                  „Intra uus vaade” on sisse lülitatud.
                </v-alert>
                <v-textarea
                  v-model="intraFooter"
                  :loading="saving === 'intraFooter'"
                  :disabled="saving !== null || !intraLook.plate"
                  label="Sisselogimise lehe jaluse tekst"
                  persistent-hint
                  hint="Näidatakse uue kujunduse allservas. Tühja välja korral jalust ei ole."
                  rows="2"
                  auto-grow
                  maxlength="300"
                  class="mt-6"
                  @blur="saveIntraFooter"
                />
              </v-expansion-panel-text>
              <!-- E-post (Resend): the plan's limits, which Resend does not report,
                   for the quota bar on Süsteem → E-post. -->
              <!-- SMS: which provider sends the reminders. The keys live in the
                   server's .env; one without them cannot be chosen. -->
              <v-expansion-panel-text v-else-if="group.sms">
                <p class="text-body-2 text-grey-darken-1">
                  Mille kaudu intra SMS-meeldetuletused saadab. Muudatus kehtib kohe
                  järgmisest SMS-ist — serverit ei ole vaja taaskäivitada.
                </p>
                <v-radio-group
                  v-model="sms.provider"
                  :disabled="saving !== null || !sms.loaded"
                  hide-details
                  class="mt-2"
                  @update:model-value="saveSmsProvider"
                >
                  <v-radio value="twilio" :disabled="!sms.available.twilio">
                    <template v-slot:label>
                      <div>
                        <div>Twilio</div>
                        <div class="text-caption text-grey-darken-1">
                          {{ sms.available.twilio ? 'Saatja on telefoninumber.' : 'Võtmed puuduvad serveris (TWILIO_…).' }}
                        </div>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio value="csc" :disabled="!sms.available.csc" class="mt-2">
                    <template v-slot:label>
                      <div>
                        <div>CSC Telecom</div>
                        <div class="text-caption text-grey-darken-1">
                          {{ sms.available.csc ? 'Saatja nimi „Gaspre”, õ saadetakse o-na, et SMS jääks üheosaliseks.' : 'Võtmed puuduvad serveris (CSC_LOGIN / CSC_API_KEY).' }}
                        </div>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
                <v-alert
                  v-if="sms.loaded && !sms.available[sms.provider]"
                  type="warning"
                  class="mt-4 mb-0"
                  variant="tonal"
                  density="compact"
                >
                  Valitud teenusepakkuja võtmed puuduvad serveris — SMS-e ei saa praegu saata.
                </v-alert>
                <p v-if="sms.loaded && !sms.chosen" class="text-caption text-grey-darken-1 mt-4 mb-0">
                  Praegu kehtib serveri vaikimisi valik (SMS_PROVIDER).
                </p>
                <v-switch
                  v-model="settings.smsForeign"
                  :loading="saving === 'smsForeign'"
                  :disabled="saving !== null"
                  inset
                  class="mt-4"
                  label="SMS välismaa numbritele"
                  persistent-hint
                  hint="Sees: meeldetuletuse SMS läheb ka numbritele, mis ei alga +372-ga (nt +358…, 0046…). Väljas: välismaa numbritele SMS-i ei saadeta, e-kiri läheb ikka."
                  @update:model-value="save('smsForeign', $event)"
                />
              </v-expansion-panel-text>
              <v-expansion-panel-text v-else-if="group.mail">
                <!-- Which sender the letters leave by, and the other one as a
                     backup. The keys live in the server's .env. -->
                <p class="text-body-2 text-grey-darken-1">
                  Mille kaudu intra e-kirjad saadab. Muudatus kehtib kohe järgmisest kirjast.
                </p>
                <v-radio-group
                  v-model="mail.provider"
                  :disabled="saving !== null || !mail.loaded"
                  hide-details
                  class="mt-2"
                  @update:model-value="saveMailProvider({ provider: $event })"
                >
                  <v-radio value="resend" :disabled="!mail.available.resend">
                    <template v-slot:label>
                      <div>
                        <div>Resend</div>
                        <div class="text-caption text-grey-darken-1">{{ mail.available.resend ? 'Süsteem → E-post näitab Resendi saadetud kirju.' : 'Võti puudub serveris (RESEND_API_KEY).' }}</div>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio value="brevo" :disabled="!mail.available.brevo" class="mt-2">
                    <template v-slot:label>
                      <div>
                        <div>Brevo</div>
                        <div class="text-caption text-grey-darken-1">{{ mail.available.brevo ? 'Tasuta kuni 300 kirja päevas.' : 'Võti puudub serveris (BREVO_API_KEY).' }}</div>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
                <v-switch
                  v-model="mail.fallback"
                  :loading="saving === 'mailFallback'"
                  :disabled="saving !== null || !mail.loaded || !mail.available[otherMailProvider]"
                  inset
                  label="Varusaatja"
                  persistent-hint
                  :hint="'Kui ' + mailName(mail.provider) + ' kirja vastu ei võta, saadetakse sama kiri ' + mailName(otherMailProvider) + ' kaudu.' + (mail.available[otherMailProvider] ? '' : ' ' + mailName(otherMailProvider) + ' võti puudub serveris.')"
                  class="mt-4"
                  @update:model-value="saveMailProvider({ fallback: $event })"
                />
                <v-alert
                  v-if="mail.loaded && !mail.available[mail.provider]"
                  type="warning"
                  class="mt-4 mb-0"
                  variant="tonal"
                  density="compact"
                >
                  Valitud saatja võti puudub serveris — e-kirju saab saata ainult varusaatja kaudu.
                </v-alert>
                <div class="text-subtitle-2 mt-8">Resendi paketi piirid</div>
                <p class="text-body-2 text-grey-darken-1">
                  Resendi paketi piirid, millega Süsteem → E-post võrdleb sel kuul ja täna
                  saadetut. Resend ise piiri ei ütle. 0 tähendab, et piiri ei näidata.
                </p>
                <v-text-field
                  v-model.number="mailLimits.monthly"
                  type="number"
                  min="0"
                  label="Kuu piir"
                  hint="Näiteks tasuta paketil 3000"
                  persistent-hint
                  :disabled="saving !== null"
                  :loading="saving === 'mailMonthly'"
                  class="mt-4"
                  @blur="saveMailLimit('monthly')"
                  @keyup.enter="saveMailLimit('monthly')"
                />
                <v-text-field
                  v-model.number="mailLimits.daily"
                  type="number"
                  min="0"
                  label="Päeva piir"
                  hint="Ainult tasuta paketil (100); tasulisel jäta 0"
                  persistent-hint
                  :disabled="saving !== null"
                  :loading="saving === 'mailDaily'"
                  class="mt-4"
                  @blur="saveMailLimit('daily')"
                  @keyup.enter="saveMailLimit('daily')"
                />
              </v-expansion-panel-text>
              <v-expansion-panel-text v-else>
                <p class="text-body-2 text-grey-darken-1">
                  Mida klient oma objekti lehel kliendiportaalis näeb. Muudatus jõustub
                  kliendi lehe uuesti avamisel.
                </p>

                <v-switch
                  v-model="portal.plate"
                  :loading="saving === 'plate'"
                  :disabled="saving !== null"
                  inset
                  label="Uus kujundus"
                  persistent-hint
                  hint="Hooldusplaadiga vaade. Kehtib, kui kliendikeskkonna uus vaade
                        on sees."
                  @update:model-value="savePortal('plate', $event)"
                />

                <!-- The look lives only in the Vue 3 portal: say so rather than let
                     a switched-on look silently do nothing. -->
                <v-alert
                  v-if="portal.plate && !settings.customerNext"
                  type="info"
                  class="mt-4 mb-0"
                  variant="tonal"
                  density="compact"
                >
                  Kliendid näevad uut kujundust alles siis, kui „Uued vaated” all on
                  sisse lülitatud „Kliendikeskkonna uus vaade”.
                </v-alert>

                <v-textarea
                  v-model="loginFooter"
                  :loading="saving === 'loginFooter'"
                  :disabled="saving !== null"
                  label="Sisselogimise lehe jaluse tekst"
                  persistent-hint
                  hint="Näidatakse uue kujunduse sisselogimise lehe allservas. Tühja
                        välja korral jalust ei ole."
                  rows="2"
                  auto-grow
                  maxlength="300"
                  class="mt-6"
                  @blur="saveText('loginFooter')"
                />

                <v-switch
                  v-model="portal.technicPhone"
                  :loading="saving === 'technicPhone'"
                  :disabled="saving !== null"
                  inset
                  label="Näita tehniku telefoni"
                  persistent-hint
                  hint="Seotud tehniku telefoninumber tehniku nime järel."
                  class="mt-6"
                  @update:model-value="savePortal('technicPhone', $event)"
                />

                <v-switch
                  v-model="portal.technicEmail"
                  :loading="saving === 'technicEmail'"
                  :disabled="saving !== null"
                  inset
                  label="Näita tehniku e-posti"
                  persistent-hint
                  hint="Seotud tehniku e-posti aadress tehniku nime järel."
                  class="mt-6"
                  @update:model-value="savePortal('technicEmail', $event)"
                />

                <v-switch
                  v-model="portal.contractPrice"
                  :loading="saving === 'contractPrice'"
                  :disabled="saving !== null"
                  inset
                  label="Näita lepingu hinda"
                  persistent-hint
                  hint="Objekti lepingu hind kliendi objekti lehel."
                  class="mt-6"
                  @update:model-value="savePortal('contractPrice', $event)"
                />

                <v-switch
                  v-model="portal.reactionTime"
                  :loading="saving === 'reactionTime'"
                  :disabled="saving !== null"
                  inset
                  label="Näita reageerimisaega"
                  persistent-hint
                  hint="Objekti reageerimisaeg kliendi objekti lehel."
                  class="mt-6"
                  @update:model-value="savePortal('reactionTime', $event)"
                />

                <v-switch
                  v-model="portal.dutyPhoneOn"
                  :loading="saving === 'dutyPhoneOn'"
                  :disabled="saving !== null"
                  inset
                  label="Näita valve/remonditehniku telefoni"
                  persistent-hint
                  hint="Üks number kõigile klientidele, seotud tehniku all. Tühja
                        numbri korral rida ei näidata."
                  class="mt-6"
                  @update:model-value="savePortal('dutyPhoneOn', $event)"
                />

                <v-text-field
                  v-model="dutyPhone"
                  :loading="saving === 'dutyPhone'"
                  :disabled="saving !== null || !portal.dutyPhoneOn"
                  label="Valve/Remonditehniku telefon"
                  maxlength="40"
                  class="mt-6"
                  @blur="saveText('dutyPhone')"
                  @keyup.enter="saveText('dutyPhone')"
                />

                <v-text-field
                  v-model.number="dailyMailLimit"
                  type="number"
                  min="0"
                  max="50"
                  label="Kliendi e-kirjade piir päevas"
                  hint="Mitu hooldusakti klient saab portaalist päevas e-postiga saata. 0 = piiranguta."
                  persistent-hint
                  :disabled="saving !== null"
                  :loading="saving === 'dailyMailLimit'"
                  class="mt-6"
                  @blur="saveDailyMailLimit"
                  @keyup.enter="saveDailyMailLimit"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </loading-content>
  </system-frame>
</template>

<script>
import SystemFrame from '@/components/SystemFrame.vue'
import api from '@/api'
import { mapActions } from 'vuex'

// The sections of the page, in order, one expansion panel each. Most are
// module switches by topic; Kliendiportaal, Intra sisselogimine and E-post are
// drawn by hand (text fields and endpoints of their own), so here they are
// only a title and a flag, to put them in their place.
//
// A switch with `parent` sits indented under that one and is greyed out while
// it is off. `warn` answers the alert under the switch, or null.
//
// Adding a switch: one entry here, plus its key in service/features.js and its
// name in MODULE_NAMES (controllers/system.js).
const GROUPS = [
  {
    title: 'Aktid',
    intro: 'Hooldusaktide moodul ja see, kuidas see töötab. Muudatus jõustub kohe — serverit ei ole vaja taaskäivitada.',
    switches: [
      {
        key: 'akt',
        label: 'Aktid',
        hint: 'Hooldusaktide moodul aadressil /akt. Väljas olekus ei ole akti vaade kättesaadav ja kalendris ei näidata nuppu "Tee akt".',
        warn: (settings, available) => settings.akt && available.akt === false
          ? { type: 'warning', text: 'Moodul on sisse lülitatud, aga selle veebiliidest ei ole serverisse ehitatud (dist/akt puudub) — aadress /akt avab tavalise Intra vaate.' }
          : null
      },
      {
        key: 'aktNext',
        parent: 'akt',
        label: 'Akti uus vaade',
        hint: 'Akti moodul avaneb uues kujunduses (Vue 3). Väljas olekus kasutatakse senist vaadet. Vahetus jõustub lehe uuesti avamisel, mõlemas suunas.',
        warn: (settings, available) => settings.aktNext && available.aktNext === false
          ? { type: 'warning', text: 'Uut vaadet ei ole serverisse ehitatud (dist/akt-next puudub) — lülita see välja või ehita akt3 valmis.' }
          : null
      },
      {
        key: 'aktPlate',
        parent: 'aktNext',
        label: 'Hooldusaktid uues kujunduses',
        hint: 'Akti mooduli nimekirjad hooldusplaadi kujunduses: telefonis all nupud numbritega, arvutis üleval sakid; igal nimekirjal üks rida (kuupäev, aadress, klient, tehnik oma värviga), Tegemata ja Pooleli ütlevad, mis on hiljaks ja mis on järgmine samm, Tehtud näitab iga akti teed arveni; ka sisselogimine. Akti vorm ise jääb samaks.'
      },
      {
        key: 'aktFormPlate',
        parent: 'aktNext',
        label: 'Akti vorm uues kujunduses',
        hint: 'Akti täitmise vorm hooldusplaadi kujunduses, eelkõige telefoni jaoks: osad nummerdatud ja kokkupandavad, üleval edenemise riba, all alati summa ning Salvesta mustand ja Allkirjasta. CO-andur ja ventilatsioon tuleb uuel aktil ise JAH või EI vajutada, Tehtud töödes on nupp „Kõik hooldustööd“, puudujäägid on enne allkirjastamist nimekirjana lehel. Allkirjastatud aktil on näha akti teekond ja kontori nupud on ühe „Kontor“ nupu all. Töötab nii uues kui vanas raamis. Välja lülitades on vorm nagu enne.'
      }
    ]
  },
  {
    title: 'Pakkumised (Merit)',
    intro: 'Aktide saatmine Merit Aktivasse pakkumisena.',
    switches: [
      {
        key: 'offer',
        label: 'Pakkumised',
        hint: 'Nupp "Saada Meritisse" akti peal: akt saadetakse Meritisse pakkumisena. Väljas olekus nuppu ei näidata.',
        warn: (settings, available) => {
          if (settings.offer && available.offer === false) {
            return { type: 'warning', text: 'Merit API ei ole serveris seadistatud (MERIT_API_ID / MERIT_API_KEY), seega pakkumise tegemine ebaõnnestub.' }
          }
          if (settings.offer && !settings.akt) {
            return { type: 'info', text: 'Pakkumise nupp asub akti peal, aga aktide moodul on välja lülitatud.' }
          }
          return null
        }
      },
      {
        key: 'offerEmail',
        parent: 'offer',
        label: 'Kliendi e-posti parandamine Meritis',
        hint: 'Kui aktil on e-mail, mida Meriti kliendil ei ole, küsitakse pärast pakkumise saatmist, millised e-mailid kliendile Meritisse jätta. Väljas olekus Meriti kliendi e-maili ei puudutata.'
      },
      {
        key: 'monthlyBill',
        parent: 'offer',
        label: 'Koondarve',
        hint: 'Akti alustades valib tehnik Tavaline või Lepinguline. Aktil on linnuke "Koondarvele" (Lepingulisel aktil alati märgitud): sellist akti ei saadeta pakkumisena, vaid kontor lisab kõik kogunenud aktid lehel Aktid → Koondarved kliendi saatmata perioodilisele arvele Meritis. Väljas olekus valikut, linnukest ega lehte ei näidata.',
        warn: (settings, available) => settings.monthlyBill && available.monthlyBill === false
          ? { type: 'warning', text: 'Merit API ei ole serveris seadistatud (MERIT_API_ID / MERIT_API_KEY).' }
          : null
      }
    ]
  },
  {
    title: 'Uued vaated',
    intro: 'Mis kujunduses Intra ja kliendikeskkond avanevad. Vahetus jõustub lehe uuesti avamisel, mõlemas suunas.',
    switches: [
      {
        key: 'clientNext',
        label: 'Intra uus vaade',
        hint: 'Intra avaneb uues kujunduses (Vue 3). Väljas olekus kasutatakse senist vaadet. Ka see lüliti on mõlemas vaates olemas.',
        warn: (settings, available) => settings.clientNext && available.clientNext === false
          ? { type: 'warning', text: 'Uut Intra vaadet ei ole serverisse ehitatud (dist/client-next puudub) — lülita see välja või ehita client3 valmis.' }
          : null
      },
      {
        key: 'intraFrame',
        parent: 'clientNext',
        label: 'Intra uus raam ja Töölaud',
        hint: 'Uus menüü, mille iga kasutaja paneb oma nime alt vasakule, paremale või üles ja saab peita, ning Töölaud uues kujunduses. Ainult intra uues vaates.'
      },
      {
        key: 'calendarPlate',
        parent: 'clientNext',
        label: 'Kalender uues kujunduses',
        hint: 'Kalender hooldusplaadi kujunduses: pealkiri ja filtrid ühel real kiipidena, töökäsud tüübi järgi toonitud ning planeerijad kiipidena. Ainult intra uues vaates.'
      },
      {
        key: 'calendarPhone',
        parent: 'clientNext',
        label: 'Kalender telefonis',
        hint: 'Telefonis (kuni 700 px) uue kujundusega Kalender: kuupäev ühel väikesel real, Päev või Nimekiri, filtrid eraldi lehel, päevanuppudel töökäskude arv ning töökäsku puudutades kaart, kust saab helistada, kaardi avada ja akti teha. Lauaarvutis ei muutu midagi.'
      },
      {
        key: 'eventPlate',
        parent: 'clientNext',
        label: 'Töökäsk uues kujunduses',
        hint: 'Töökäsu aken hooldusplaadi kujunduses: näha on, mis täideti kalendrist või objekti andmetest, kas tehnik on sel ajal vaba, ning aadressi tulevased ja tehtud töökäsud. Ainult intra uues vaates.'
      },
      {
        key: 'objectPlate',
        parent: 'clientNext',
        label: 'Objekt uues kujunduses',
        hint: 'Objekti leht, objektide nimekiri, duplikaatide otsing ning objekti lisamise ja muutmise vorm hooldusplaadi kujunduses: leping plaadina, hoolduse riba (viimane hooldus, järgmine tähtaeg, oranž kui tähtaeg on möödas), sakkidel arvud ja „Viimati siin“; nimekirjas liigid arvudega, neli arvu üleval, otsing ja filtrid tabeli kohal ning igal real hoolduse seis. Ainult intra uues vaates.'
      },
      {
        key: 'clientPlate',
        parent: 'clientNext',
        label: 'Kliendid uues kujunduses',
        hint: 'Klientide nimekiri ja kliendi leht hooldusplaadi kujunduses: nimekirjas liigid arvudega, kolm arvu üleval (maksmata tööd, probleemsed, objektita), üks otsing ja filtrid, järjestus ning igal real kontakt, objektid, viimane töökäsk ja maksmata tööd; kliendi lehel sakkidel arvud, objektid ja ajalugu, kus on ainult päris muudatused (vana väärtus läbi kriipsutatud uue kõrval). Ainult intra uues vaates.'
      },
      {
        key: 'userPlate',
        parent: 'clientNext',
        label: 'Kasutajad uues kujunduses',
        hint: 'Kasutajad hooldusplaadi kujunduses: kõik ühel lehel oma värviga, rollid, viimane sisselogimine, viimase 30 päeva ja järgmine töökäsk ning kas planeerija, allkiri ja Google on seadistatud; vormis rollid selgitusega ja värv nii, nagu Kalender seda joonistab, koos hoiatusega, kui sama värv on juba kellelgi. Ainult intra uues vaates.'
      },
      {
        key: 'plannerPlate',
        parent: 'clientNext',
        label: 'Planeerija uues kujunduses',
        hint: 'Planeerija hooldusplaadi kujunduses: üks kuu nädalapäevade ja riigipühadega, pintslid lühendi ja kuu arvuga, lahtris täht ja pehmem värv, valvajate rida (oranž, kui tööpäeval pole keegi valves), töökäsud samas tabelis ning nähtav salvestamine ja „Võta tagasi“. Igaüks saab lehelt või Minu seadetest jääda vana vaate juurde. Ainult intra uues vaates.'
      },
      {
        key: 'dialogPlate',
        parent: 'clientNext',
        label: 'Dialoogid uues kujunduses',
        hint: 'Lisa märkus, kustutamise kinnitus ja allkirja aken hooldusplaadi kujunduses, nii vanadel kui uutel lehtedel: märkuse aknas märkus või hooldus kahe nupuna, tähtajad nuppudena (vaikimisi vanim lahtine), sammud kahes tulbas koos „Märgi kõik“, fotod pisipiltidena ja kokkuvõte; kinnitus ütleb, mida kustutatakse, ja nupp on tegu; allkirjal on näha, kelle oma, ja joon. Välja lülitades on kõik kolm nagu enne. Ainult intra uues vaates.'
      },
      {
        key: 'systemPlate',
        parent: 'clientNext',
        label: 'Süsteemi lehed uues kujunduses',
        hint: 'Süsteemi lehed (Seaded, Varundus, Meeldetuletused, SMS, E-post, Turvalisus, Linnad ja Riigid) hooldusplaadi kujunduses: pealkiri ütleb, milleks leht on, üleval sakid lehtede vahel, kaardid, tabelid ja nupud uues stiilis. Ainult intra uues vaates.'
      },
      {
        key: 'customerNext',
        label: 'Kliendikeskkonna uus vaade',
        hint: 'Kliendikeskkond avaneb uues kujunduses (Vue 3). Väljas olekus kasutatakse senist vaadet.',
        warn: (settings, available) => settings.customerNext && available.customerNext === false
          ? { type: 'warning', text: 'Uut kliendikeskkonda ei ole serverisse ehitatud (dist/customer-next puudub) — lülita see välja või ehita customer3 valmis.' }
          : null
      }
    ]
  },
  {
    title: 'Haldusfirma',
    intro: 'Lepingulise objekti haldusfirma Kliendi (korteriühistu) kõrval. Muudatus jõustub kohe.',
    switches: [
      {
        key: 'manager',
        label: 'Haldusfirma objektil',
        hint: 'Lepingulise objekti vormil on väli Haldusfirma. Haldusfirma logib kliendiportaali sama lepingu numbri ja oma telefoniga ning näeb kõiki maju, kus ta on Klient või Haldusfirma; Klient näeb ainult oma maja. Kliendi lehel on loetelu „Objektid haldusfirmana“. Objektil saab valida, kas aktid saadetakse Kliendile, Haldusfirmale või mõlemale (akti „Saada e-postiga“ küsib, kellele) ja kas Koondarve maksja on Klient või Haldusfirma. Välja lülitades on kõik nagu enne ja salvestatud haldusfirmad jäävad alles.'
      }
    ]
  },
  { title: 'Ladu', ladu: true },
  { title: 'Kliendiportaal', portal: true },
  { title: 'Intra sisselogimine', intraLogin: true },
  { title: 'E-post', mail: true },
  { title: 'SMS', sms: true }
]

export default {
  components: { SystemFrame },
  data () {
    return {
      loading: true,
      saving: null,
      // Which sections are open, by index. Empty is every one closed, which
      // is how the page opens.
      open: [],
      settings: { akt: false, offer: false, offerEmail: false, monthlyBill: false, aktNext: false, customerNext: false, clientNext: false, intraFrame: false, eventPlate: false, objectPlate: false, clientPlate: false, calendarPlate: false, calendarPhone: false, userPlate: false, plannerPlate: false, dialogPlate: false, systemPlate: false, aktPlate: false, aktFormPlate: false, manager: false, ladu: false, smsForeign: false },
      available: {},
      // Ladu: the saved address and whether a key is saved, the two fields' own
      // copies, what "Testi ühendust" last said, and the technicians' vans.
      ladu: { baseUrl: '', apiKeySet: false },
      laduLoaded: false,
      laduUrl: '',
      laduKey: '',
      laduTest: null,
      vans: { loaded: false, loading: false, technics: [], stores: [], note: null },
      portal: { technicPhone: true, technicEmail: false, contractPrice: false, reactionTime: false, dutyPhoneOn: true, dutyPhone: '', plate: false, loginFooter: '', dailyMailLimit: 2 },
      // The fields' own copies, so typing does not count as saved until it is.
      dutyPhone: '',
      dailyMailLimit: 2,
      loginFooter: '',
      // Resend's plan limits for the E-post quota bar, and the saved copy.
      // Intra's own login look, and the footer field's own copy.
      intraLook: { plate: false, loginFooter: '' },
      intraFooter: '',
      mailLimits: { monthly: 0, daily: 0 },
      mailLimitsSaved: { monthly: 0, daily: 0 },
      // Which provider sends the SMS, and which ones have their keys.
      sms: { provider: null, chosen: false, available: {}, loaded: false },
      // Which sender the e-mail leaves by, and the backup.
      mail: { provider: null, fallback: false, available: {}, loaded: false },
      smsSaved: null,
      mailSaved: { provider: null, fallback: false }
    }
  },
  computed: {
    GROUPS: () => GROUPS,
    otherMailProvider () {
      return this.mail.provider === 'brevo' ? 'resend' : 'brevo'
    }
  },
  methods: {
    ...mapActions(['showNotification', 'getFeatures']),
    // A warning (not an info) under any of the section's switches.
    hasWarning (group) {
      if (group.mail) {
        return this.mail.loaded && !this.mail.available[this.mail.provider]
      }
      if (group.ladu) {
        return this.settings.ladu && this.laduLoaded && (!this.ladu.baseUrl || !this.ladu.apiKeySet)
      }
      if (group.sms) {
        return this.sms.loaded && !this.sms.available[this.sms.provider]
      }
      return (group.switches || []).some(item => {
        const warning = item.warn && item.warn(this.settings, this.available)
        return warning && warning.type === 'warning'
      })
    },
    async save (key, value) {
      this.saving = key
      try {
        const { settings } = await api.updateSystemSettings({ [key]: value })
        // Take the server's word for the new state, so a rejected change can
        // never leave the switch showing something that is not in force.
        this.settings = settings
        // The left menu asks the same question; a module just switched on must
        // not stay missing from it until this tab is reloaded.
        this.getFeatures()
        this.showNotification({ text: 'Seadistus salvestatud', color: 'success' })
      } catch (err) {
        this.settings = { ...this.settings, [key]: !value }
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = null
      }
    },
    async savePortal (key, value) {
      this.saving = key
      try {
        const { portal } = await api.updatePortalSettings({ [key]: value })
        this.portal = portal
        this.dutyPhone = portal.dutyPhone
        this.loginFooter = portal.loginFooter
        this.dailyMailLimit = portal.dailyMailLimit
        this.showNotification({ text: 'Seadistus salvestatud', color: 'success' })
      } catch (err) {
        if (key === 'dutyPhone' || key === 'loginFooter' || key === 'dailyMailLimit') this[key] = this.portal[key]
        else this.portal = { ...this.portal, [key]: !value }
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = null
      }
    },
    async saveIntraLook (key, value) {
      this.saving = key === 'plate' ? 'intraPlate' : 'intraFooter'
      try {
        const { look } = await api.updateIntraLookSettings({ [key]: value })
        this.intraLook = look
        this.intraFooter = look.loginFooter
        this.showNotification({ text: 'Seadistus salvestatud', color: 'success' })
      } catch (err) {
        if (key === 'plate') this.intraLook = { ...this.intraLook, plate: !value }
        else this.intraFooter = this.intraLook.loginFooter
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = null
      }
    },
    // Ladu's address saves when left, and only when it changed.
    async saveLaduUrl () {
      const value = (this.laduUrl || '').trim()
      if (this.saving !== null || value === this.ladu.baseUrl) return
      await this.saveLadu('laduUrl', { baseUrl: value })
    },
    async saveLaduKey () {
      const value = (this.laduKey || '').trim()
      if (this.saving !== null || !value) return
      await this.saveLadu('laduKey', { apiKey: value })
    },
    async saveLadu (what, change) {
      this.saving = what
      try {
        const { ladu } = await api.updateLaduSettings(change)
        this.ladu = ladu
        this.laduUrl = ladu.baseUrl
        this.laduKey = ''
        this.laduTest = null
        // Another Ladu, or another key: the vans on screen may be someone else's.
        this.vans = { loaded: false, loading: false, technics: [], stores: [], note: null }
        this.showNotification({ text: 'Seadistus salvestatud', color: 'success' })
      } catch (err) {
        this.laduUrl = this.ladu.baseUrl
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = null
      }
    },
    async testLadu () {
      this.saving = 'laduTest'
      this.laduTest = null
      try {
        this.laduTest = await api.testLadu()
      } catch (err) {
        this.laduTest = { ok: false, message: err.message }
      } finally {
        this.saving = null
      }
    },
    async loadVans () {
      this.vans = { ...this.vans, loading: true }
      try {
        this.vans = { ...(await api.getLaduVans()), loaded: true, loading: false }
      } catch (err) {
        this.vans = { ...this.vans, loading: false }
        this.showNotification({ text: err.message, color: 'red' })
      }
    },
    // The stores a technician can be given: his own and the free ones. One that
    // is somebody else's is left out, so a van cannot be taken from under him
    // by a slip of the hand.
    storeChoices (technic) {
      const free = this.vans.stores.filter(store =>
        !store.technicianId || String(store.technicianId) === String(technic.id))
      return [
        { title: '— ei ole autot —', value: null },
        ...free.map(store => ({ title: store.name + (store.type === 'MAIN' ? ' (Keskladu)' : ''), value: store.id }))
      ]
    },
    async saveVan (technic, storeId) {
      this.saving = 'van' + technic.id
      try {
        this.vans = { ...(await api.updateLaduVan(technic.id, storeId || null)), loaded: true, loading: false }
        this.showNotification({ text: 'Seadistus salvestatud', color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
        // What is on screen may no longer be what Ladu holds.
        this.loadVans()
      } finally {
        this.saving = null
      }
    },
    mailName (provider) {
      return provider === 'brevo' ? 'Brevo' : 'Resend'
    },
    async saveMailProvider (change) {
      const before = { ...this.mailSaved }
      this.saving = change.fallback !== undefined ? 'mailFallback' : 'mailProvider'
      try {
        const status = await api.updateMailProvider(change)
        this.mail = { ...status, loaded: true }
        this.mailSaved = { provider: status.provider, fallback: status.fallback }
        this.showNotification({ text: 'Seadistus salvestatud', color: 'success' })
      } catch (err) {
        this.mail = { ...this.mail, ...before }
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = null
      }
    },
    async saveSmsProvider (provider) {
      // The radio has already moved; this is what to put back on a refusal.
      const before = this.smsSaved
      this.saving = 'smsProvider'
      try {
        const status = await api.updateSmsProvider(provider)
        this.sms = { ...status, loaded: true }
        this.smsSaved = status.provider
        this.showNotification({ text: 'SMS-e saadab nüüd ' + (provider === 'csc' ? 'CSC Telecom' : 'Twilio'), color: 'success' })
      } catch (err) {
        this.sms = { ...this.sms, provider: before }
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = null
      }
    },
    // Saved when left, and only when it changed.
    saveIntraFooter () {
      const value = (this.intraFooter || '').trim()
      if (this.saving !== null || value === this.intraLook.loginFooter) return
      this.saveIntraLook('loginFooter', value)
    },
    // The two limits save when left, and only when they changed.
    async saveMailLimit (key) {
      const value = Math.max(0, Math.floor(Number(this.mailLimits[key]) || 0))
      if (this.saving !== null || value === this.mailLimitsSaved[key]) {
        this.mailLimits[key] = this.mailLimitsSaved[key]
        return
      }
      this.saving = key === 'monthly' ? 'mailMonthly' : 'mailDaily'
      try {
        const { limits } = await api.updateMailLimits({ [key]: value })
        this.mailLimits = { ...limits }
        this.mailLimitsSaved = { ...limits }
        this.showNotification({ text: 'Seadistus salvestatud', color: 'success' })
      } catch (err) {
        this.mailLimits[key] = this.mailLimitsSaved[key]
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = null
      }
    },
    // Saved when left, and only when it changed; a blank or bad number goes back.
    saveDailyMailLimit () {
      const raw = this.dailyMailLimit
      const value = Number(raw)
      if (raw === '' || raw === null || !Number.isInteger(value) || value < 0 || value > 50) {
        this.dailyMailLimit = this.portal.dailyMailLimit
        if (raw !== '' && raw !== null) this.showNotification({ text: 'Päevane piir peab olema 0–50', color: 'red' })
        return
      }
      if (this.saving !== null || value === this.portal.dailyMailLimit) return
      this.savePortal('dailyMailLimit', value)
    },
    // The two text fields save when left, and only when they changed.
    saveText (key) {
      const value = (this[key] || '').trim()
      if (this.saving !== null || value === this.portal[key]) return
      this.savePortal(key, value)
    }
  },
  async mounted () {
    try {
      const [{ settings, available }, { portal }] = await Promise.all([
        api.getSystemSettings(),
        api.getPortalSettings()
      ])
      this.settings = settings
      this.available = available || {}
      this.portal = portal
      this.dutyPhone = portal.dutyPhone
      this.loginFooter = portal.loginFooter
      this.dailyMailLimit = portal.dailyMailLimit
    } catch (err) {
      this.showNotification({ text: err.message, color: 'red' })
    } finally {
      this.loading = false
    }
    // Its own request too: only what intra has saved — Ladu itself is not asked
    // until somebody presses a button in the section.
    try {
      const { ladu } = await api.getLaduSettings()
      this.ladu = ladu
      this.laduUrl = ladu.baseUrl
      this.laduLoaded = true
    } catch (err) {
      // The section keeps its empty fields; saving will say what is wrong.
    }
    // Its own request, like the limits below.
    try {
      const { look } = await api.getIntraLookSettings()
      this.intraLook = look
      this.intraFooter = look.loginFooter
    } catch (err) {
      // The card keeps its defaults; saving will say what is wrong.
    }
    // Its own request, like the look above.
    try {
      const status = await api.getMailProvider()
      this.mail = { ...status, loaded: true }
      this.mailSaved = { provider: status.provider, fallback: status.fallback }
    } catch (err) {
      // The radios stay disabled; the page has nothing to switch.
    }
    try {
      const status = await api.getSmsProvider()
      this.sms = { ...status, loaded: true }
      this.smsSaved = status.provider
    } catch (err) {
      // The radios stay disabled; the page has nothing to switch.
    }
    // Its own request: the limits are worth showing even when the rest failed.
    try {
      const { limits } = await api.getMailLimits()
      this.mailLimits = { ...limits }
      this.mailLimitsSaved = { ...limits }
    } catch (err) {
      // The card keeps its zeros; saving will say what is wrong.
    }
  }
}
</script>
